/**
 * src/pages/venue/PickerPage.tsx — 2304vc
 *
 * Two-pass yes / no / hold picker for venue media review.
 *
 * Changes from 2304vb:
 * - Full-bleed image rendering (object-cover, no black bars)
 * - AI score fetched from venue_picks and shown on each frame
 * - Reviewed thumbnails strip at top — tap to revisit a decision
 * - iOS-feel redesign: frosted glass, lighter palette, smooth transitions
 * - Tag overlay redesigned — score pill + tag line always visible
 * - energy_level config expanded (includes 'peak', 'intimate')
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Check, X, Bookmark, ChevronLeft, ShieldAlert, Star, Settings } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Spinner } from '@/components/ui'
import { cn } from '@/components/ui/utils'
import type { Venue } from '@/types'
import type { PickerSession } from '@/lib/venue'

interface PickerPageProps { venue: Venue; picker: PickerSession; onBack: () => void }

type Decision    = 'yes' | 'no' | 'hold'
type InputMethod = 'swipe' | 'tap' | 'keyboard'
type Role        = 'club_picker' | 'club_manager'
type NavVariant  = 'buttons' | 'zones' | 'bar' | 'minimal'

interface InflowTag {
  id:                  string
  media_item_id:       string
  tag_line:            string | null
  mood:                string | null
  energy_level:        string | null
  approval_signal:     string | null
  identified_act:      string | null
  scene_type:          string | null
  quality_score:       number | null
  overall_score:       number | null
  vibe_notes:          string | null
  subject_description: string | null
}

interface PickFrame {
  id:             string
  cloudinary_url: string
  tag:            InflowTag | null
  ai_score:       number | null
  ai_reasoning:   Record<string, unknown> | null
}

interface PickAction {
  frame_id:          string
  decision:          Decision
  method:            InputMethod
  time_on_frame_ms:  number
  sequence_position: number
}


const TAG_SELECT = 'id, media_item_id, tag_line, mood, energy_level, approval_signal, identified_act, scene_type, quality_score, overall_score, vibe_notes, subject_description'

// Supabase returns either an array or a single object for related rows
// depending on whether a FK relationship is defined. Handle both.
function firstTag(raw: unknown): InflowTag | null {
  if (!raw) return null
  if (Array.isArray(raw)) return (raw[0] as InflowTag) ?? null
  return raw as InflowTag
}

function firstPick(raw: unknown): { ai_score: number | null; ai_reasoning: Record<string, unknown> | null } | null {
  if (!raw) return null
  if (Array.isArray(raw)) return (raw[0] as any) ?? null
  return raw as any
}

// Scene type → sort group (lower = earlier in queue)
function sceneGroup(sceneType: string | null | undefined): number {
  if (!sceneType) return 99
  if (['performer', 'dj_booth'].includes(sceneType))        return 2
  if (['crowd', 'crowd_social'].includes(sceneType))         return 3
  if (['group_social', 'portrait'].includes(sceneType))      return 4
  if (['vip_area', 'atmosphere', 'venue_architecture'].includes(sceneType)) return 5
  return 6
}

async function fetchPickFrames(venueId: string, _pass: number): Promise<PickFrame[]> {
  const { data, error } = await supabase
    .from('media_items')
    .select(`id, cloudinary_url, inflow_tags(${TAG_SELECT}), venue_picks(ai_score, ai_reasoning, picker_action)`)
    .eq('venue_id', venueId)
  if (error) throw new Error(`fetchPickFrames: ${error.message}`)

  const frames = (data ?? [])
    .filter((m: any) => {
      // Only show unreviewed photos — exclude anything already actioned
      const pick = firstPick(m.venue_picks)
      const action = (pick as any)?.picker_action
      return !action || action === null  // null = not yet picked
    })
    .map((m: any) => ({
      id:             m.id,
      cloudinary_url: m.cloudinary_url,
      tag:            firstTag(m.inflow_tags),
      ai_score:       firstPick(m.venue_picks)?.ai_score ?? null,
      ai_reasoning:   firstPick(m.venue_picks)?.ai_reasoning ?? null,
    }))

  // Sort: cream (top 15% by score) first, then by scene group, then by score desc
  const scores     = frames.map(f => f.ai_score ?? 0)
  const maxScore   = Math.max(...scores, 0)
  const creamCutoff = maxScore * 0.85  // top 15% = cream

  return frames.sort((a, b) => {
    const aScore = a.ai_score ?? 0
    const bScore = b.ai_score ?? 0
    const aCream = aScore >= creamCutoff ? 0 : 1
    const bCream = bScore >= creamCutoff ? 0 : 1
    if (aCream !== bCream) return aCream - bCream        // cream first
    if (aCream === 0) return bScore - aScore              // within cream: score desc
    const aGroup = sceneGroup(a.tag?.scene_type)
    const bGroup = sceneGroup(b.tag?.scene_type)
    if (aGroup !== bGroup) return aGroup - bGroup         // then scene group
    return bScore - aScore                                 // within group: score desc
  })
}

async function createPickSession(params: {
  venueId: string; role: Role; passNumber: number; pickerName: string
}): Promise<string> {
  const { data, error } = await supabase.from('pick_sessions').insert({
    venue_id:    params.venueId,
    role:        params.role,
    pass_number: params.passNumber,
    picker_name: params.pickerName,
    started_at:  new Date().toISOString(),
  }).select('id').single()
  if (error) throw error
  return data.id
}

// Map picker decision → venue_picks.picker_action value
function decisionToAction(decision: Decision): string {
  if (decision === 'yes')  return 'saved'          // selected for review/posting
  if (decision === 'no')   return 'rejected'        // not selected
  return 'needs_approval'                           // hold = needs another look
}

async function recordPickAction(params: {
  sessionId: string; venueId: string; action: PickAction; role: Role; passNumber: number
}) {
  // Write to pick_actions (full audit log)
  await supabase.from('pick_actions').insert({
    pick_session_id:   params.sessionId,
    venue_id:          params.venueId,
    media_item_id:     params.action.frame_id,
    role:              params.role,
    pass_number:       params.passNumber,
    decision:          params.action.decision,
    method:            params.action.method,
    time_on_frame_ms:  params.action.time_on_frame_ms,
    sequence_position: params.action.sequence_position,
    decided_at:        new Date().toISOString(),
  })

  // Update venue_picks.picker_action — row already exists from ingest/AI tagger
  const action = decisionToAction(params.action.decision)
  const { data: existing } = await supabase
    .from('venue_picks')
    .select('id')
    .eq('venue_id', params.venueId)
    .eq('media_item_id', params.action.frame_id)
    .single()

  if (existing?.id) {
    await supabase.from('venue_picks')
      .update({ picker_action: action, actioned_at: new Date().toISOString() })
      .eq('id', existing.id)
  } else {
    // No row yet — create one
    await supabase.from('venue_picks').insert({
      venue_id:      params.venueId,
      media_item_id: params.action.frame_id,
      picker_action: action,
      actioned_at:   new Date().toISOString(),
      created_at:    new Date().toISOString(),
    })
  }
}

async function closePickSession(sessionId: string, counts: { yes: number; no: number; hold: number }) {
  await supabase.from('pick_sessions').update({
    completed_at: new Date().toISOString(),
    frame_count:  counts.yes + counts.no + counts.hold,
    yes_count:    counts.yes,
    no_count:     counts.no,
    hold_count:   counts.hold,
  }).eq('id', sessionId)
}

function ScorePill({ score }: { score: number | null }) {
  if (score === null) return null
  const pct = Math.round(score * 100)
  const color =
    pct >= 80 ? 'bg-emerald-500/90 text-white' :
    pct >= 60 ? 'bg-amber-500/90 text-black'   :
                'bg-white/20 text-white'
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ', color)}>
      <Star size={9} fill="currentColor" />{pct}
    </span>
  )
}

function ApprovalPill({ signal, onTap }: { signal: string | null; onTap?: () => void }) {
  if (!signal || signal === 'clear') return null
  return (
    <button type="button" onClick={onTap}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold
                 bg-amber-500/90 text-black  active:scale-95 transition-transform
                 pointer-events-auto">
      <ShieldAlert size={9} />{signal === 'needs_approval' ? 'Approval needed — tap' : 'Check'}
    </button>
  )
}

function energyDot(level: string | null): string {
  const map: Record<string, string> = {
    peak: 'bg-emerald-400', high: 'bg-yellow-400',
    medium: 'bg-orange-400', intimate: 'bg-purple-400', low: 'bg-blue-400',
  }
  return map[level?.toLowerCase() ?? ''] ?? 'bg-white/40'
}

function RoleSelector({ onSelect }: { onSelect: (role: Role, pass: number) => void }) {
  return (
    <div className="fixed inset-0 bg-black flex flex-col items-center justify-center px-6 gap-3">
      <div className="text-center mb-2">
        <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-4">
          <Bookmark size={22} className="text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white tracking-tight">Who's picking?</h2>
        <p className="text-white/40 text-sm mt-1">Sets your pass and what you see</p>
      </div>
      {([
        { role: 'club_picker'  as Role, pass: 1, label: 'Club Picker',       sub: 'All frames · fast pass' },
        { role: 'club_manager' as Role, pass: 2, label: 'Club Manager',      sub: 'Holds from pass 1' },
      ] as const).map(({ role, pass, label, sub }) => (
        <button key={role} type="button" onClick={() => onSelect(role, pass)}
          className="w-full max-w-xs bg-white/6 border border-white/10 rounded-2xl px-5 py-4 text-left
                     hover:bg-white/10 hover:border-white/20 active:scale-98 transition-all duration-150">
          <p className="text-white font-semibold text-[15px]">{label}</p>
          <p className="text-white/40 text-xs mt-0.5">{sub}</p>
        </button>
      ))}
    </div>
  )
}

function DoneScreen({ counts, pass: _pass, onBack }: { counts: { yes: number; no: number; hold: number }; pass: number; onBack: () => void }) {
  const total = counts.yes + counts.no + counts.hold
  return (
    <div className="fixed inset-0 bg-black flex flex-col items-center justify-center px-6 gap-6 text-center">
      <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
        <Check size={28} className="text-emerald-400" strokeWidth={2.5} />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-white">Picking done</h2>
        <p className="text-white/40 text-sm mt-1">{counts.yes} selected from {total} reviewed</p>
      </div>
      <div className="flex gap-12">
        <div><p className="text-3xl font-black text-emerald-400">{counts.yes}</p><p className="text-white/35 text-xs mt-1">Selected</p></div>
        <div><p className="text-3xl font-black text-white/30">{counts.no + counts.hold}</p><p className="text-white/35 text-xs mt-1">Passed</p></div>
      </div>
      {/* Primary CTA — go to shortlist */}
      <button type="button" onClick={onBack}
        style={{ width: '100%', maxWidth: '280px', padding: '16px', borderRadius: '16px',
                 background: 'white', color: 'black', fontWeight: 700, fontSize: '16px',
                 border: 'none', cursor: 'pointer' }}>
        View shortlist →
      </button>
      <p className="text-white/25 text-xs -mt-3">Share with your marketing team</p>
    </div>
  )
}

export function PickerPage({ venue, picker, onBack }: PickerPageProps) {
  const [role, setRole]             = useState<Role | null>(null)
  const [pass, setPass]             = useState(1)
  const [sessionId, setSessionId]   = useState<string | null>(null)
  const navigate   = useNavigate()
  const sessionKey = `picker_${venue.id}_${pass}`
  const [index, setIndex]           = useState(0)  // always start at 0 — frames change
  const [counts, setCounts]         = useState(() => {
    const saved = sessionStorage.getItem(`${sessionKey}_counts`)
    return saved ? JSON.parse(saved) : { yes: 0, no: 0, hold: 0 }
  })
  const [done, setDone]             = useState(false)
  const [navVariant, setNavVariant] = useState<NavVariant>(() => {
    return (localStorage.getItem('picker-nav-variant') as NavVariant) ?? 'buttons'
  })
  const [showVariantPicker, setShowVariantPicker] = useState(false)
  const [showNotes, setShowNotes] = useState<boolean>(() =>
    localStorage.getItem('picker-show-notes') !== 'false'
  )


  const setAndSaveVariant = (v: NavVariant) => {
    setNavVariant(v)
    localStorage.setItem('picker-nav-variant', v)
    setShowVariantPicker(false)
  }

  const toggleNotes = () => {
    setShowNotes(v => {
      localStorage.setItem('picker-show-notes', String(!v))
      return !v
    })
  }
  const frameEnteredAt              = useRef<number>(Date.now())

  const { data: frames = [], isLoading } = useQuery({
    queryKey: ['picker-frames', venue.id, pass],
    queryFn:  () => fetchPickFrames(venue.id, pass),
    enabled:  !!role,
    staleTime: Infinity,
  })

  // Preload first 5 frames on data load using Image objects (no DOM thrash)
  useEffect(() => {
    if (!frames.length) return
    frames.slice(0, 5).forEach(f => {
      const img = new Image()
      img.src = f.cloudinary_url.replace('/upload/', '/upload/q_auto,f_auto,w_1200/')
    })
  }, [frames.length])

  // Preload images aggressively — current + next 10 frames
  useEffect(() => {
    if (!frames.length) return
    // Preload a window of frames around current position
    const toLoad = frames.slice(index, index + 3)
    toLoad.forEach(f => {
      // Main image
      const img = new Image()
      img.src = f.cloudinary_url
    })
  }, [frames, index])

  useEffect(() => {
    if (!role) return
    createPickSession({ venueId: venue.id, role, passNumber: pass, pickerName: picker.display_name })
      .then(setSessionId).catch(console.error)
  }, [role])

  useEffect(() => {
    frameEnteredAt.current = Date.now()
    sessionStorage.setItem(`${sessionKey}_index`, String(index))
  }, [index, sessionKey])

  const decide = useCallback((decision: Decision, method: InputMethod) => {
    if (!role || !frames.length || index >= frames.length) return
    const frame = frames[index]
    const timeMs = Date.now() - frameEnteredAt.current
    const action: PickAction = { frame_id: frame.id, decision, method, time_on_frame_ms: timeMs, sequence_position: index }



    setCounts((prev: { yes: number; no: number; hold: number }) => {
      const next = { ...prev, [decision]: prev[decision] + 1 }
      sessionStorage.setItem(`${sessionKey}_counts`, JSON.stringify(next))
      const nextIndex = index + 1
      if (nextIndex >= frames.length) {
        if (sessionId) closePickSession(sessionId, next)
        sessionStorage.removeItem(`${sessionKey}_index`)
        sessionStorage.removeItem(`${sessionKey}_counts`)
        setDone(true)
      } else {
        setIndex(nextIndex)
      }
      return next
    })

    if (sessionId) recordPickAction({ sessionId, venueId: venue.id, action, role, passNumber: pass })
  }, [role, frames, index, sessionId, venue.id, pass])

  useEffect(() => {
    if (!role || done) return
    const h = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') decide('yes',  'keyboard')
      if (e.key === 'ArrowLeft')  decide('no',   'keyboard')
      if (e.key === 'ArrowUp')    decide('hold', 'keyboard')
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [decide, role, done])

  const touchStart      = useRef<{ x: number; y: number } | null>(null)
  const pickerDivRef    = useRef<HTMLDivElement>(null)
  const navigatingAway  = useRef(false)

  // Trap browser back/forward — picker owns navigation while mounted
  useEffect(() => {
    const onPop = () => {
      if (!navigatingAway.current) {
        window.history.pushState({ picker: true }, '')
      }
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0]
    touchStart.current = { x: t.clientX, y: t.clientY }
  }
  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart.current || !role || done) return
    const t = e.changedTouches[0]
    const dx = t.clientX - touchStart.current.x
    const dy = t.clientY - touchStart.current.y
    const THRESH = 60
    if (Math.abs(dx) > THRESH && Math.abs(dx) > Math.abs(dy)) decide(dx > 0 ? 'yes' : 'no', 'swipe')
    else if (Math.abs(dy) > THRESH && dy < 0)                  decide('hold', 'swipe')
    touchStart.current = null
  }

  if (!role) return <RoleSelector onSelect={(r, p) => { if (r === 'club_manager') { onBack(); return; } setRole(r); setPass(p); }} />
  if (isLoading) return <div className="fixed inset-0 bg-black flex items-center justify-center"><Spinner size={32} /></div>

  if (!frames.length) return (
    <div className="fixed inset-0 bg-black flex flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="text-4xl">📭</p>
      <p className="text-white font-semibold">Nothing to review</p>
      <p className="text-white/45 text-sm">
        {pass === 2 ? 'No holds from pass 1 yet.' : pass === 3 ? 'No frames flagged for approval.' : 'No frames for this venue.'}
      </p>
      <button type="button" onClick={onBack}
        className="mt-2 px-5 py-2.5 rounded-xl bg-white/8 text-white text-sm hover:bg-white/12 transition-colors">
        Back
      </button>
    </div>
  )

  if (done) return <DoneScreen counts={counts} pass={pass} onBack={onBack} />

  const frame = frames[index]
  const tag   = frame.tag
  const img   = frame.cloudinary_url

  return (
    <div ref={pickerDivRef}
      className="fixed inset-0 bg-black flex flex-col select-none overflow-hidden"
      style={{ touchAction: 'none' }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}>

      {/* Top bar */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0">
        {/* Back — solid pill, clearly visible */}
        <button
          type="button"
          onClick={() => { navigatingAway.current = true; navigate(-1) }}
          onTouchEnd={(e) => { e.stopPropagation(); navigatingAway.current = true; navigate(-1) }}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '12px 24px', borderRadius: '999px',
            background: 'white', color: 'black',
            fontWeight: 600, fontSize: '14px',
            border: 'none', cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          }}>
          <ChevronLeft size={20} strokeWidth={3} />
          Back
        </button>
        <div className="text-center">
          <p className="text-white/70 text-sm font-semibold">{index + 1} / {frames.length}</p>
        </div>
        {/* Nav style switcher */}
        <button type="button" onClick={() => setShowVariantPicker(v => !v)}
          className="w-8 h-8 rounded-full bg-white/8 flex items-center justify-center text-white/50 hover:text-white transition-colors relative">
          <Settings size={15} />
          {showVariantPicker && (
            <div className="absolute top-10 right-0 bg-black/90 border border-white/15 rounded-2xl p-2 w-52 z-50 "
              onClick={e => e.stopPropagation()}>
              <p className="text-white/40 text-[10px] uppercase tracking-widest px-2 pb-1.5">Nav style</p>
              {([
                { v: 'buttons' as NavVariant, label: 'Floating buttons',   sub: 'Buttons below image' },
                { v: 'bar'     as NavVariant, label: 'Bottom bar',         sub: 'Full-width tap targets' },
                { v: 'zones'   as NavVariant, label: 'Tap zones',          sub: 'Left=Pass · Right=Select' },
                { v: 'minimal' as NavVariant, label: 'Gesture only',       sub: 'Swipe hints, no buttons' },
              ]).map(({ v, label, sub }) => (
                <button key={v} type="button" onClick={() => setAndSaveVariant(v)}
                  className={cn('w-full text-left px-2 py-2 rounded-xl transition-all',
                    navVariant === v ? 'bg-white/15 text-white' : 'text-white/50 hover:bg-white/8 hover:text-white/80',
                  )}>
                  <p className="text-[12px] font-medium">{label}</p>
                  <p className="text-[10px] text-white/35">{sub}</p>
                </button>
              ))}
              {/* Notes toggle */}
              <div className="border-t border-white/8 mt-1 pt-1">
                <button type="button" onClick={toggleNotes}
                  className="w-full flex items-center justify-between px-2 py-2 rounded-xl hover:bg-white/8 transition-all">
                  <div>
                    <p className="text-[12px] font-medium text-white/70 text-left">AI notes</p>
                    <p className="text-[10px] text-white/35 text-left">Reasoning text on image</p>
                  </div>
                  <div className={cn(
                    'w-8 h-5 rounded-full transition-all relative',
                    showNotes ? 'bg-emerald-500/80' : 'bg-white/15',
                  )}>
                    <div className={cn(
                      'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all',
                      showNotes ? 'left-3.5' : 'left-0.5',
                    )} />
                  </div>
                </button>
              </div>

              {/* Reset — demo tool */}
              <div className="border-t border-white/8 mt-1 pt-1">
                <button type="button"
                  onClick={() => {
                    Object.keys(sessionStorage)
                      .filter(k => k.includes(venue.id) || k.startsWith('picker_'))
                      .forEach(k => sessionStorage.removeItem(k))
                    setShowVariantPicker(false)
                    setIndex(0)
                    setCounts({ yes: 0, no: 0, hold: 0 })
                                    setDone(false)
                  }}
                  className="w-full flex items-center px-2 py-2 rounded-xl hover:bg-red-500/10 transition-all text-left">
                  <div>
                    <p className="text-[12px] font-medium text-red-400">Reset session</p>
                    <p className="text-[10px] text-red-400/50">Clear all picks · start from frame 1</p>
                  </div>
                </button>
              </div>
            </div>
          )}
        </button>
      </div>



      {/* Reviewed strip */}

      {/* Image */}
      <div className="flex-1 relative mx-4 mb-3 rounded-2xl overflow-hidden min-h-0"
        style={{ boxShadow: '0 0 0 1px rgba(255,255,255,0.06)' }}>

        {/* ZONES variant — invisible tap regions on image */}
        {navVariant === 'zones' && (
          <div className="absolute inset-0 z-30 flex pointer-events-auto">
            <button type="button" aria-label="No"  onClick={() => decide('no',   'tap')} className="flex-1 h-full" />
            <button type="button" aria-label="Hold" onClick={() => decide('hold', 'tap')} onTouchEnd={(e) => { e.stopPropagation(); decide('hold', 'tap') }} className="flex-1 h-full" />
            <button type="button" aria-label="Yes" onClick={() => decide('yes',  'tap')} className="flex-1 h-full" />
          </div>
        )}

        {img
          ? <>

              {/* Main image contained — true to original, no cropping */}
              <img key={frame.id} src={img} alt={tag?.tag_line ?? ''}
                className="relative w-full h-full object-contain"
                style={{ transition: 'opacity 0.15s ease' }}
                draggable={false}
              />
            </>
          : <div className="w-full h-full bg-white/5 flex items-center justify-center">
              <Spinner size={24} />
            </div>
        }



        {/* Top-right: score + approval */}
        <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5 pointer-events-none z-20">
          <ScorePill score={frame.ai_score} />
          <ApprovalPill signal={tag?.approval_signal ?? null} />
        </div>

        {/* Top-left: energy */}
        {tag?.energy_level && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5 pointer-events-none z-20">
            <span className={cn('w-2 h-2 rounded-full', energyDot(tag.energy_level))} />
            <span className="text-white/65 text-[10px] font-medium capitalize">{tag.energy_level}</span>
          </div>
        )}

        {/* Bottom: tag info */}
        <div className="absolute bottom-0 inset-x-0 pointer-events-none z-20"
          style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.5) 50%, transparent 100%)' }}>
          <div className="px-4 pb-5 pt-16">
            {/* Tag line — always visible */}
            {tag?.tag_line && (
              <p style={{ color: 'white', fontWeight: 700, fontSize: '10px', lineHeight: 1.3, marginBottom: '3px' }}>
                {tag.tag_line}
              </p>
            )}
            {/* AI reasoning notes — togglable via settings */}
            {showNotes && typeof frame.ai_reasoning?.notes === 'string' && (
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '9px', lineHeight: 1.4, marginBottom: '3px' }}>
                {String(frame.ai_reasoning.notes)}
              </p>
            )}
            {/* Metadata chips */}
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
              {tag?.mood && (
                <span className="text-white/70 text-[12px] capitalize bg-white/10 px-2 py-0.5 rounded-full">{tag.mood}</span>
              )}
              {tag?.energy_level && (
                <span className="text-white/70 text-[12px] capitalize bg-white/10 px-2 py-0.5 rounded-full">{tag.energy_level}</span>
              )}
              {tag?.scene_type && (
                <span className="text-white/40 text-[11px] bg-white/8 px-2 py-0.5 rounded-full">{tag.scene_type.replace(/_/g, ' ')}</span>
              )}
              {tag?.identified_act && (
                <span className="text-amber-300 text-[11px] font-semibold">{tag.identified_act}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Nav variants ── */}

      {/* BUTTONS — floating circles */}
      {navVariant === 'buttons' && (
        <div className="flex items-center justify-center gap-4 px-6 pb-6 shrink-0">
          <button type="button" onClick={() => decide('no', 'tap')} onTouchEnd={(e) => { e.stopPropagation(); decide('no', 'tap') }} aria-label="No"
            className="w-16 h-16 rounded-full border border-white/12 bg-white/6
                       flex items-center justify-center text-white/50
                       hover:border-red-400/60 hover:text-red-400 hover:bg-red-500/10
                       active:scale-90 transition-all duration-150">
            <X size={24} strokeWidth={1.8} />
          </button>
          <button type="button" onClick={() => {}} aria-label="Info"
            className="w-14 h-14 rounded-full border border-white/12 bg-white/6
                       flex items-center justify-center text-white/50
                       hover:border-amber-400/60 hover:text-amber-400 hover:bg-amber-500/10
                       active:scale-90 transition-all duration-150">
            <Bookmark size={20} strokeWidth={1.8} />
          </button>
          <button type="button" onClick={() => decide('yes', 'tap')} onTouchEnd={(e) => { e.stopPropagation(); decide('yes', 'tap') }} aria-label="Yes"
            className="w-16 h-16 rounded-full border border-white/12 bg-white/6
                       flex items-center justify-center text-white/50
                       hover:border-emerald-400/60 hover:text-emerald-400 hover:bg-emerald-500/10
                       active:scale-90 transition-all duration-150">
            <Check size={24} strokeWidth={1.8} />
          </button>
        </div>
      )}

      {/* BAR — full-width labelled buttons */}
      {navVariant === 'bar' && (
        <div className="flex gap-2 px-4 pb-5 shrink-0">
          <button type="button" onClick={() => decide('no', 'tap')} onTouchEnd={(e) => { e.stopPropagation(); decide('no', 'tap') }} aria-label="No"
            className="flex-1 py-4 rounded-2xl border border-white/10 bg-white/5
                       flex flex-col items-center justify-center gap-1
                       hover:bg-red-500/12 hover:border-red-400/40 active:scale-98 transition-all">
            <X size={20} strokeWidth={1.8} className="text-white/50" />
            <span className="text-[10px] text-white/35 font-medium">No</span>
          </button>
          <button type="button" onClick={() => {}} aria-label="Info"
            className="flex-1 py-4 rounded-2xl border border-white/10 bg-white/5
                       flex flex-col items-center justify-center gap-1
                       hover:bg-amber-500/12 hover:border-amber-400/40 active:scale-98 transition-all">
            <Bookmark size={20} strokeWidth={1.8} className="text-white/50" />
            
          </button>
          <button type="button" onClick={() => decide('yes', 'tap')} onTouchEnd={(e) => { e.stopPropagation(); decide('yes', 'tap') }} aria-label="Yes"
            className="flex-1 py-4 rounded-2xl border border-white/10 bg-white/5
                       flex flex-col items-center justify-center gap-1
                       hover:bg-emerald-500/12 hover:border-emerald-400/40 active:scale-98 transition-all">
            <Check size={20} strokeWidth={1.8} className="text-white/50" />
            <span className="text-[10px] text-white/35 font-medium">Yes</span>
          </button>
        </div>
      )}

      {/* ZONES — invisible tap regions overlaid on image (no bottom bar) */}
      {navVariant === 'zones' && (
        <>
          {/* Tap zone hint row */}
          <div className="flex items-center justify-between px-6 pb-4 shrink-0 pointer-events-none">
            <span className="text-[10px] text-white/25">← No</span>
            
            <span className="text-[10px] text-white/25">Yes →</span>
          </div>
        </>
      )}

      {/* MINIMAL — swipe hints only, no buttons, no bottom bar */}
      {navVariant === 'minimal' && index === 0 && (
        <div className="flex items-center justify-center gap-6 pb-5 shrink-0 pointer-events-none">
          <span className="text-[10px] text-white/20">← No</span>
          
          <span className="text-[10px] text-white/20">Yes →</span>
        </div>
      )}
      {navVariant === 'minimal' && index > 0 && (
        <div className="pb-4 shrink-0" />
      )}
    </div>
  )
}
